import React from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { Document } from '@contentful/rich-text-types';
import { SearchType } from '@zoopla/landing-pages/types';
import {
    styledImgStyle,
    imgContainerStyle,
    heroContainerStyle,
} from './Hero.styles.css';
import { contentStyle, titleStyle } from './HeroContent.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { CONTENTFUL_RICH_TEXT_OPTIONS } from '@zoopla/cms/contentful-render-options';
import Head from 'next/head';

interface BaseProps {
    image: string;
    title: string;
    children: React.ReactNode;
    quality?: number;
    slug?: SearchType;
    showHero?: boolean;
}

interface HeroWithRichText {
    description?: never;
    richTextDescription: Document;
}

interface HeroWithPlainText {
    description: string;
    richTextDescription?: never;
}

type Props = BaseProps & (HeroWithRichText | HeroWithPlainText);

const maxMobileWidth = 550;
const mobileMedia = `(max-width: ${maxMobileWidth}px)`;
const nonMobileMedia = `(min-width: ${maxMobileWidth + 1}px)`;

export const Hero = ({
    image,
    quality = 55,
    slug,
    showHero = true,
    title,
    description,
    richTextDescription,
    children,
}: Props) => {
    const isHousePrices = slug === 'house-prices';

    // The "breakpoint" at which the page layout stops
    // being adaptive and turns to a fixed width layout
    const ZDS_LAYOUT_MAXWIDTH = '1224px';

    const avifSrcSet = `${image}?w=320&fm=avif&q=${quality} 320w,
    ${image}?w=768&fm=avif&q=${quality} 768w,
    ${image}?w=1024&fm=avif&q=${quality} 1024w,
    ${image}?w=1224&fm=avif&q=${quality} 1224w,
    ${image}?w=1400&fm=avif&q=${quality} 1400w,
    ${image}?w=1900&fm=avif&q=${quality} 1900w,`;

    const avifMobileSrcSet = `${image}?w=320&fm=avif&q=${quality}, ${image}?w=768&fm=avif&q=${quality} 2x`;

    const webpSrcSet = `${image}?w=320&fm=webp&q=${quality} 320w,
    ${image}?w=768&fm=webp&q=${quality} 768w,
    ${image}?w=1024&fm=webp&q=${quality} 1024w,
    ${image}?w=1224&fm=webp&q=${quality} 1224w,
    ${image}?w=1400&fm=webp&q=${quality} 1400w,
    ${image}?w=1900&fm=webp&q=${quality} 1900w,`;

    const webpMobileSrcSet = `${image}?w=320&fm=webp&q=${quality}, ${image}?w=768&fm=webp&q=${quality} 2x`;

    return (
        <>
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href=""
                    imageSrcSet={avifSrcSet}
                    imageSizes={`(min-width: ${ZDS_LAYOUT_MAXWIDTH}) 1224px, 100vw`}
                    media={nonMobileMedia}
                />
                <link
                    rel="preload"
                    as="image"
                    href=""
                    imageSrcSet={avifMobileSrcSet}
                    imageSizes="100vw"
                    media={mobileMedia}
                />
            </Head>
            <Box position="relative">
                <div
                    className={imgContainerStyle({
                        homeValuesResult: !showHero,
                    })}
                >
                    <picture style={{ display: 'unset' }}>
                        <source
                            srcSet={avifMobileSrcSet}
                            sizes="100w"
                            type="image/avif"
                            media={mobileMedia}
                        />
                        <source
                            srcSet={avifSrcSet}
                            sizes={`(min-width: ${ZDS_LAYOUT_MAXWIDTH}) 1224px, 100vw`}
                            type="image/avif"
                            media={nonMobileMedia}
                        />
                        <source
                            srcSet={webpMobileSrcSet}
                            sizes="100w"
                            type="image/webp"
                            media={mobileMedia}
                        />
                        <source
                            srcSet={webpSrcSet}
                            sizes={`(min-width: ${ZDS_LAYOUT_MAXWIDTH}) 1224px, 100vw`}
                            type="image/webp"
                            media={nonMobileMedia}
                        />
                        <img
                            className={styledImgStyle}
                            src={`${image}?w=1024&fm=jpg&q=${quality}&fl=progressive`}
                            alt=""
                        />
                    </picture>
                </div>

                <div
                    className={heroContainerStyle({
                        homeValuesResult: !showHero,
                        isHousePrices,
                    })}
                >
                    <div
                        className={contentStyle({
                            homeValuesResult: !showHero,
                            isHousePrices,
                        })}
                    >
                        {showHero && (
                            <h1 className={`${titleStyle} ${fontGroups.mug} `}>
                                {title}
                            </h1>
                        )}

                        {showHero &&
                            richTextDescription &&
                            documentToReactComponents(
                                richTextDescription,
                                CONTENTFUL_RICH_TEXT_OPTIONS
                            )}

                        {showHero && description && (
                            <p className={fontGroups.ladle}>{description}</p>
                        )}

                        {children}
                    </div>
                </div>
            </Box>
        </>
    );
};
